import type { PaperWithRole } from "@/types/paper";

// Scoring weights (matching Taipy pdf_matcher.py)
const TITLE_WEIGHT = 0.70;
const AUTHOR_WEIGHT = 0.20;
const DOI_WEIGHT = 0.10;

// Confidence thresholds
const HIGH_CONFIDENCE = 80;
const MEDIUM_CONFIDENCE = 60;

export interface MatchResult {
  paper: PaperWithRole;
  confidence: number;
  titleScore: number;
  authorScore: number;
  doiScore: number;
  category: "high" | "medium" | "low";
}

/**
 * Simple ratio — Levenshtein-based similarity (0-100).
 */
function ratio(a: string, b: string): number {
  if (a === b) return 100;
  if (a.length === 0 || b.length === 0) return 0;

  // Levenshtein distance via two-row DP
  const m = a.length;
  const n = b.length;
  let prev = new Array(n + 1);
  let curr = new Array(n + 1);
  for (let j = 0; j <= n; j++) prev[j] = j;

  for (let i = 1; i <= m; i++) {
    curr[0] = i;
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
    }
    [prev, curr] = [curr, prev];
  }

  const distance = prev[n];
  const maxLen = Math.max(m, n);
  return Math.round(((maxLen - distance) / maxLen) * 100);
}

/**
 * Token set ratio — faithful port of RapidFuzz's algorithm.
 * Compares the intersection and remainder tokens of two strings.
 */
export function tokenSetRatio(s1: string, s2: string): number {
  const tokens1 = new Set(normalizeString(s1).split(/\s+/).filter(Boolean));
  const tokens2 = new Set(normalizeString(s2).split(/\s+/).filter(Boolean));

  if (tokens1.size === 0 && tokens2.size === 0) return 100;
  if (tokens1.size === 0 || tokens2.size === 0) return 0;

  const intersection = new Set([...tokens1].filter((t) => tokens2.has(t)));
  const diff1 = [...tokens1].filter((t) => !tokens2.has(t));
  const diff2 = [...tokens2].filter((t) => !tokens1.has(t));

  const sorted = (arr: string[]) => [...arr].sort().join(" ");
  const intersectionStr = sorted([...intersection]);
  const combined1 = sorted([...intersection, ...diff1]);
  const combined2 = sorted([...intersection, ...diff2]);

  return Math.max(
    ratio(intersectionStr, combined1),
    ratio(intersectionStr, combined2),
    ratio(combined1, combined2),
  );
}

export function normalizeString(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function extractLastName(author: string): string {
  const trimmed = author.trim();
  const parts = trimmed.split(",");
  if (parts.length > 1) {
    return normalizeString(parts[0]);
  }
  const words = trimmed.split(/\s+/);
  return normalizeString(words[words.length - 1] || trimmed);
}

export function normalizeDoi(doi: string | null): string {
  if (!doi) return "";
  return doi
    .toLowerCase()
    .trim()
    .replace("https://doi.org/", "")
    .replace("http://doi.org/", "")
    .replace("doi:", "")
    .trim();
}

function calculateTitleScore(
  extractedTitle: string,
  paperTitle: string,
): number {
  return tokenSetRatio(extractedTitle, paperTitle);
}

function calculateAuthorScore(
  extractedAuthors: string[],
  paperAuthors: string[],
): number {
  if (extractedAuthors.length === 0 || paperAuthors.length === 0) return 0;

  const pdfNames = extractedAuthors.map(extractLastName);
  const paperNames = paperAuthors.map(extractLastName);

  let matches = 0;
  for (const pdfName of pdfNames) {
    for (const paperName of paperNames) {
      if (tokenSetRatio(pdfName, paperName) >= 85) {
        matches++;
        break;
      }
    }
  }

  const maxPossible = Math.max(pdfNames.length, paperNames.length);
  return maxPossible > 0 ? (matches / maxPossible) * 100 : 0;
}

function calculateDoiScore(
  extractedDoi: string | null,
  paperDoi: string | null,
): number {
  const a = normalizeDoi(extractedDoi);
  const b = normalizeDoi(paperDoi);
  if (!a || !b) return 0;
  return a === b ? 100 : 0;
}

function categorize(confidence: number): "high" | "medium" | "low" {
  if (confidence >= HIGH_CONFIDENCE) return "high";
  if (confidence >= MEDIUM_CONFIDENCE) return "medium";
  return "low";
}

/**
 * Match an extracted PDF title/authors against a list of papers.
 * Returns matches sorted by confidence (highest first).
 *
 * When a DOI extracted from the PDF matches a paper's DOI exactly,
 * the confidence is boosted to at least 95 (definitive match).
 */
export function matchPdfToPapers(
  extractedTitle: string | null,
  extractedAuthors: string[],
  papers: PaperWithRole[],
  maxMatches = 5,
  extractedDoi: string | null = null,
): MatchResult[] {
  if (
    papers.length === 0 ||
    (!extractedTitle && extractedAuthors.length === 0 && !extractedDoi)
  ) {
    return [];
  }

  const results: MatchResult[] = [];

  for (const paper of papers) {
    const titleScore = extractedTitle
      ? calculateTitleScore(extractedTitle, paper.title)
      : 0;
    const authorScore = calculateAuthorScore(extractedAuthors, paper.authors);
    const doiScore = calculateDoiScore(extractedDoi, paper.doi);

    let confidence: number;
    if (doiScore === 100) {
      // DOI match is definitive — override weighted score
      confidence = Math.max(
        95,
        Math.round(
          titleScore * TITLE_WEIGHT +
            authorScore * AUTHOR_WEIGHT +
            doiScore * DOI_WEIGHT,
        ),
      );
    } else {
      confidence = Math.min(
        100,
        Math.round(
          titleScore * TITLE_WEIGHT +
            authorScore * AUTHOR_WEIGHT +
            doiScore * DOI_WEIGHT,
        ),
      );
    }

    results.push({
      paper,
      confidence,
      titleScore,
      authorScore,
      doiScore,
      category: categorize(confidence),
    });
  }

  results.sort((a, b) => b.confidence - a.confidence);
  return results.slice(0, maxMatches);
}
